<template>
  <div class="p-event-grid-view">
    <v-data-table
      :headers="tableHeaders"
      :items="eventsMapped"
      :search="search"
      :page.sync="page"
      :items-per-page="itemsPerPage"
      :sort-by="['start']"
      no-data-text=""
      hide-default-footer
      class="p-no-borders"
      @page-count="pageCount = $event"
    >
      <template v-slot:[`header.category.name`]="{ header }">
        {{ header.text }}
        <v-tooltip v-if="hasAccess() && hasAccess('write', 'settings')" bottom slot="append-outer">
          <template v-slot:activator="{ on }">
            <router-link :to="{ name: 'settingsEventCategories' }">
              <v-icon class="ml-2" v-on="on">mdi-cog-outline</v-icon>
            </router-link>
          </template>
          {{ $t('event.manageCategories') }}
        </v-tooltip>
      </template>
      <template v-slot:[`item.start`]="{ item }">
        <v-list-item class="px-0">
          <v-list-item-avatar :size="40" :color="item.isCurrent ? 'blue darken-1' : ''">
            <span :class="{ 'subtitle-1': true, 'white--text': item.isCurrent }">{{ item.dayFormatted }}</span>
          </v-list-item-avatar>
          <v-list-item-content :class="{ 'text-uppercase': true, 'pGrey--text': !item.isCurrent, 'blue--text darken-1--text': item.isCurrent}">
            {{ item.dateFormatted }}
          </v-list-item-content>
        </v-list-item>
      </template>
      <template v-slot:[`item.allDay`]="{ item }">
        {{ item.timeFormatted }}
      </template>
        <template v-slot:[`item.rRule`]="{ item }">
        <v-tooltip bottom v-if="item.rRule">
          <template v-slot:activator="{ on }">
            <v-icon v-on="on">mdi-repeat</v-icon>
          </template>
          <span>{{ item.rRuleText }}</span>
        </v-tooltip>
      </template>
      <template v-slot:[`item.changed`]="{ item }">
        <v-tooltip v-if="item.changed" bottom>
          <template v-slot:activator="{ on }">
            <v-btn icon v-on="on">
              <span v-if="item.changed" class="item-changed"></span>
            </v-btn>
          </template>
          {{ $t('event.table.changedTooltip') }}
        </v-tooltip>
      </template>
      <template v-slot:[`item.labelsMapped`]="{ item }">
        <div class="p-chips-wrapper">
          <v-chip
            v-for="label of item.labelsMapped"
            :key="label.id"
            small
            outlined
            label
          >
            {{ label.name }}
          </v-chip>
        </div>
      </template>
      <template v-slot:[`item.members`]="{ item }">
        <span class="d-flex my-2">
          <span v-for="i in 3" :key="i">
          <v-tooltip
            v-if="i <= item.members.length"
            bottom
          >
            <template v-slot:activator="{ on }">
              <span v-on="on" class="mr-2">
                <p-avatar
                  :char="item.membersMapped[i-1].displayName[0]"
                  :color="item.membersMapped[i-1].color"
                  :email="item.membersMapped[i-1].email"
                ></p-avatar>
              </span>
            </template>
            <span>{{ item.membersMapped[i-1].displayNameWithCompany }}</span>
          </v-tooltip>
          </span>
        </span>
        <span v-if="item.members.length > 3" class="pGrey--text">+{{item.members.length-3}}</span>
      </template>
      <template v-slot:[`item.isPromoter`]="{ item }">
        <v-icon v-if="item.isPromoter" color="primary">mdi-check</v-icon>
      </template>
      <template v-slot:[`item.audience`]="{ item }">
        <span>{{ item.audience }}</span>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <div class="p-table-row-actions p-visible-on-row-hover">
          <template v-if="hasAccess()">
            <p-btn-icon type="edit" @click="$emit('edit-event', item, true)"></p-btn-icon>
            <p-btn-icon type="copy" @click="$emit('copy-event', item, true)"></p-btn-icon>
            <p-btn-icon type="delete" @click="$emit('delete-event', item)"></p-btn-icon>
          </template>
          <template v-else>
            <p-btn-icon type="view" @click="$emit('edit-event', item, true)"></p-btn-icon>
          </template>
        </div>
      </template>
      <template v-slot:[`no-data`]>
        <p-no-data
          v-if="eventsByProjectLoaded(currentProjectId)"
          img="event.png"
          class="p-no-data-mt"
          :text="$t('event.noEvents')"
          :btn-text="$t('event.createEvent')"
          :hide-btn="!hasAccess()"
          @btn-click="$emit('create-event', null, false)"
        ></p-no-data>
      </template>
    </v-data-table>

    <v-pagination
      v-if="eventsMapped.length > 0"
      v-model="page"
      :length="pageCount"
      :total-visible="15"
      class="mt-5"
    ></v-pagination>
  </div>
</template>


<script>
import { mapGetters } from 'vuex'

import { RRule } from 'rrule'

import moment from 'moment'

import PAvatar from '@/components/PAvatar'
import PNoData from '@/components/PNoData'
import PBtnIcon from '@/components/PBtnIcon'

import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PAvatar,
    PNoData,
    PBtnIcon
  },


  mixins: [
    accessMixin
  ],

  props: {
    search: String
  },


  data () {
    return {
      page: 1,
      pageCount: 0,
      itemsPerPage: 20
    }
  },


  computed: {
    ...mapGetters('event', ['eventsByProject', 'eventsByProjectLoaded']),
    ...mapGetters('eventLabel', ['eventLabelById']),
    ...mapGetters('eventCategory', ['eventCategoryById']),
    ...mapGetters('contact', ['shortContactById']),
    ...mapGetters('project', ['currentProjectId']),
    ...mapGetters('changes', ['isItemChanged']),

    tableHeaders () {
      return [
        { text: this.$t('event.table.date'), value: 'start', filterable: false },
        { text: this.$t('event.table.time'), value: 'allDay', filterable: false },
        { text: '', value: 'rRule', sortable: false, filterable: false },
        { text: this.$t('event.table.title'), value: 'title' },
        { text: '', value: 'changed' },
        { text: this.$t('event.table.category'), value: 'category.name' },
        {
          text: this.$t('event.table.labels'),
          value: 'labelsMapped',
          filter: (value, search) => {
            if (!search || !value) return true
            return value.find(x => x.name?.indexOf(search)) !== -1
          }
        },
        { text: this.$t('event.table.location'), value: 'googlePlace', filterable: false },
        { text: this.$t('event.table.note'), value: 'note', filterable: false },
        { text: this.$t('event.table.members'), value: 'members', filterable: false },
        { text: this.$t('event.table.promoter'), value: 'isPromoter', align: 'center', filterable: false },
        { text: this.$t('event.table.audience'), value: 'audience', align: 'center', filterable: false },
        { text: '', value: 'actions', align: 'end', sortable: false, filterable: false }
      ]
    },

    eventsMapped () {
      return this.eventsByProject(this.currentProjectId).map(event => {
        const rRuleText = event.rRule ? RRule.fromString(event.rRule).toText() : null
        const start = event.allDay ? moment(event.start, 'YYYY-MM-DD') : moment.utc(event.start).local()
        const stop = event.allDay ? moment(event.stop, 'YYYY-MM-DD') : moment.utc(event.stop).local()

        const isCurrent = event.allDay === true
          ? moment().isBetween(start, stop, 'day')
          : moment().isBetween(start, stop)

        const dayFormatted = start.format('D')
        const dateFormatted = start.format('MMMM, dddd')

        let timeFormatted = this.$t('event.allDay')
        if (!event.allDay) {
          const startTime = start.format('HH:mm')
          const stopTime = stop.format('HH:mm')
          timeFormatted = startTime + ' - ' + stopTime
        }

        const labelsMapped = event.labels.map(label => this.eventLabelById(label) || {})
        const category = this.eventCategoryById(event.categoryId) || {}
        const membersMapped = event.members.map(member => this.shortContactById(member.contactId) || {})

        return {
          ...event,
          isCurrent,
          dayFormatted,
          dateFormatted,
          timeFormatted,
          rRuleText,
          labelsMapped,
          membersMapped,
          category,
          changed: this.isItemChanged(event.id)
        }
      })
    }
  }
}
</script>
